import { LocalizationUtil } from "@/main/util/LocalizationUtil";

/**
 * FI-2814 (work order 2026-10-15 round 116 D15, slice `1c`): what a stalled or offline soft navigation ends in, instead
 * of a skeleton that never resolves or a full load that offline swaps for the browser's error page.
 *
 * <p>FI-3330 (r136 A5, ⚑G-27, r137): a non-destructive BANNER over the page being read. Until 2026-10-19 this replaced
 * the navigation root's content with an inline block, so the page the member was reading was gone while the address bar
 * still named it, and Back skipped past it. Now the page stays (the caller puts a skeleton's held page back first) and
 * the banner is a fixed element on `<body>`, outside the navigation root: rule 32, JS may not subtract the page.
 *
 * <p>FI-3413 (r137): Retry takes focus when the banner appears (`preventScroll`, so the page does not jump), and while a
 * retry runs it is `aria-disabled` with a guard, never native `disabled` (which drops focus to `<body>`). A second
 * failure re-arms the SAME banner, so focus stays on Retry.
 *
 * <p>Plain DOM, not a React island, on purpose: it renders exactly when the network is what failed, so it cannot
 * depend on a lazy chunk (which would fail the same way), and `EnhancedNavigation` is already DOM-level code. The copy
 * comes from the typed root localizer (rule 11); the control is a real `<button>` in the design language's pill recipe:
 * control boundary, the one hover wash, the rule-4 focus ring, the coarse-pointer tap floor. The banner is flat-X: a
 * border, no shadow.
 *
 * <p>Design: `designs/04-live/enhanced-navigation-current-implementation-design-2026-04-19.md` §11.4.
 */
export const NAVIGATION_LOAD_FAILED_ATTRIBUTE = "data-navigation-load-failed";
export const NAVIGATION_LOAD_FAILED_RETRY_HOOK = "navigation-load-failed-retry";

const BANNER_CLASS = "fixed inset-x-4 z-50 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left";
/** Above the phone bottom bar and the home-indicator safe area, as the phone toasts are (FI-1281). */
const BANNER_BOTTOM = "calc(var(--mobile-bottom-nav-height, 0px) + env(safe-area-inset-bottom, 0px) + 1rem)";
const MESSAGE_CLASS = "min-w-0 flex-1 text-sm text-foreground";
const RETRY_CLASS = "standard-hover inline-flex h-9 shrink-0 items-center justify-center rounded-full border border-control-boundary bg-card px-4 text-sm font-medium text-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/70 aria-disabled:opacity-60 [@media(pointer:coarse)]:min-h-11";

type LoadFailureBanner = {
    element: HTMLElement;
    retry: HTMLButtonElement;
    onRetry: () => void;
};

let activeBanner: LoadFailureBanner | null = null;

function focusRetry(retry: HTMLButtonElement): void {
    try {
        retry.focus({ preventScroll: true });
    } catch {
        retry.focus();
    }
}

/**
 * Show the Retry banner (or re-arm the one already showing, for a retry that failed again). Never touches the
 * navigation root. Returns false only when there is no document body to render into.
 */
export function showNavigationLoadFailure(onRetry: () => void): boolean {
    if (typeof document === "undefined" || !document.body) {
        return false;
    }

    if (activeBanner && activeBanner.element.isConnected) {
        activeBanner.onRetry = onRetry;
        activeBanner.retry.setAttribute("aria-disabled", "false");
        focusRetry(activeBanner.retry);
        return true;
    }
    clearNavigationLoadFailure();

    const element = document.createElement("div");
    element.setAttribute(NAVIGATION_LOAD_FAILED_ATTRIBUTE, "true");
    element.setAttribute("role", "alert");
    element.className = BANNER_CLASS;
    element.style.bottom = BANNER_BOTTOM;

    const message = document.createElement("p");
    message.className = MESSAGE_CLASS;
    message.textContent = LocalizationUtil.get("navigation.load_failed");

    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = RETRY_CLASS;
    retry.setAttribute("data-playwright-id", NAVIGATION_LOAD_FAILED_RETRY_HOOK);
    retry.setAttribute("aria-disabled", "false");
    retry.textContent = LocalizationUtil.get("literal.retry");

    const banner: LoadFailureBanner = { element, retry, onRetry };
    retry.addEventListener("click", () => {
        if (retry.getAttribute("aria-disabled") === "true") {
            return;
        }
        retry.setAttribute("aria-disabled", "true");
        banner.onRetry();
    });

    element.append(message, retry);
    document.body.append(element);
    activeBanner = banner;
    focusRetry(retry);
    return true;
}

/** Removes the banner: a page landed, or the member started another navigation. */
export function clearNavigationLoadFailure(): void {
    activeBanner?.element.remove();
    activeBanner = null;
}
